import { Channel } from "../Com/Com.Interfaces";
import { FMSType } from "../FMS/FMS.Interfaces";
import { WeeklyPeriod } from "../Schedule/Schedule.Interface";
import { OrgBaseInterface } from "../Shared/OrgBase.Interace";
import { LanguageCode } from "../UnitsAndLocalization/Languages.Interface";
import { BotSequenceMode, BotSequenceType } from "./BotSequence.Enums";

// LINK libs/entities/src/BotSequences/BotSequenceConfig.Entity.ts

/**
 * SequenceConfig (ENG-2987) — the cascading configuration object of the new sequence
 * engine (docs/snowy/plans/2026-08-03-sequence-config-object.md). One row per
 * (scope, sequence type); scope is exactly one of org / group / location, the same
 * three-level shape `location_variable` uses.
 *
 * Resolution is FIELD-BY-FIELD, location → group → org, first non-null wins — except the
 * ladder, which cascades as one unit (inherited whole or replaced whole, never merged
 * day by day). A location with no row at any scope resolves to the org default; an org
 * with nothing configured for a type means the scheduler books nothing for it.
 *
 * WHEN a message may go out is deliberately NOT configured here (decided 2026-08-19):
 * send hours, holidays and weekend behavior all live in the Schedule system (the Hours
 * page — `ScheduleInterface`, `ScheduleExceptionInterface`, `HolidayRuleInterface`) under
 * the schedule this config points at via `scheduleSlug`. A day the schedule is closed
 * defers a step, never drops it; open periods are always intersected with the legal
 * quiet-hours floor, never widening it.
 */

/**
 * The schedule slug sequence sending hours resolve to when a config expresses no
 * preference — one shared "Sequence Sending Hours" form per location on the Hours page.
 */
export const BOT_SEQUENCE_DEFAULT_SCHEDULE_SLUG = "sequence";

// The default weekly hours for the "sequence" schedule live with the other schedule groups:
// `defaultWeeklyForSlug("sequence")` in Schedule.Interface.ts (ENG-3216 decision 49).

/**
 * The channels a step delivery may use — the subset of the platform `Channel` enum the
 * engine places outbound (ENG-3216). A TypeScript subset rather than a new enum: the
 * strings are what `bot_sequence_config.channel` and the attempt rows already store.
 */
export type BotSequenceDeliveryChannel = Channel.SMS | Channel.VOICE;
export const BOT_SEQUENCE_DELIVERY_CHANNELS: readonly BotSequenceDeliveryChannel[] = [Channel.SMS, Channel.VOICE];

/**
 * Locales a verbatim body may be authored in. `en` is REQUIRED on every verbatim body set —
 * it is the fallback when the recipient's locale has no body (never translated at send time).
 */
export const BOT_SEQUENCE_BODY_LOCALES: readonly LanguageCode[] = [LanguageCode.EN, LanguageCode.ES];
export const BOT_SEQUENCE_DEFAULT_BODY_LOCALE: LanguageCode = LanguageCode.EN;

/** Write-time size ceilings (characters). One place, so the validator and the editor agree. */
export const BOT_SEQUENCE_STEP_LIMITS = {
  guidance: 2000,
  /** 10 SMS segments; also the voice script and voicemail ceiling. */
  verbatimBody: 1600,
  emailSubject: 200,
  emailBody: 20000,
  /** SiteLink appends into one free-text field — keep notes short. */
  fmsNoteText: 2000
} as const;

/**
 * Variables that exist only at fire time (not VarDef entries): supplied by the engine when
 * it renders a step's texts. Offered by the picker and accepted by the validator.
 */
export const BOT_SEQUENCE_VAR_KEYS = [
  "Sequence:Type",
  "Sequence:Day",
  "Sequence:Date",
  "Sequence:Channels",
  "Sequence:Outcomes",
  "Sequence:MessageText"
] as const;
export type BotSequenceVarKey = (typeof BOT_SEQUENCE_VAR_KEYS)[number];

/** Human labels for the fire-time variables (the picker shows `Sequence - <label>`). */
export const BOT_SEQUENCE_VAR_LABELS: Record<BotSequenceVarKey, string> = {
  "Sequence:Type": "Sequence Type",
  "Sequence:Day": "Day Number",
  "Sequence:Date": "Send Date",
  "Sequence:Channels": "Channels Used",
  "Sequence:Outcomes": "Delivery Outcomes",
  "Sequence:MessageText": "Message Text"
};

/**
 * The raw FMS lease statuses the past-due scheduler excludes unless an operator opts them back
 * in (PROD-559; SSMCloud). Lower-case, as the engine normalizes them. One source for the engine
 * gate and the Sequences page's option list.
 */
export const BOT_SEQUENCE_INACTIVE_LEASE_GATED_STATUSES: readonly string[] = ["overlock", "auction"];

/** The default FMS note template (ENG-3216 decision 26). The editor prefills it; the engine uses it when the text is blank. */
export const BOT_SEQUENCE_FMS_NOTE_DEFAULT_TEXT = "Swivl {{Sequence:Type}} sequence, Day {{Sequence:Day}} ({{Sequence:Date}}): {{Sequence:Outcomes}}";

/**
 * What one delivery says. `generated` renders via the LLM path with optional operator
 * guidance; `verbatim` is deterministic `{{ }}` substitution into an authored body —
 * no LLM anywhere in that path. `bodies` is keyed by locale so a Spanish notice is an
 * authored, reviewable body, never a translation performed at send time.
 */
export type BotSequenceConfigStepContent = { mode: "generated"; guidance?: string } | { mode: "verbatim"; bodies: Record<string, string> };

/**
 * Voice only (ENG-3216 decision 35): what the bot says after the beep when a machine
 * answers. Exact wording per locale, `{{ }}` variables allowed. `enabled: false` = the bot
 * hangs up on a machine without leaving anything.
 */
export interface BotSequenceConfigStepVoicemail {
  enabled: boolean;
  bodies: Record<string, string>;
}

/**
 * One channel of one step's fire. A step may deliver on several channels (decided
 * 2026-08-03) — a voice script and an SMS body are not the same text, so content lives
 * here, not on the step. A multi-delivery step is still ONE fire (one attempt row).
 */
export interface BotSequenceConfigStepDelivery {
  channel: BotSequenceDeliveryChannel;
  content: BotSequenceConfigStepContent;
  /** SMS only: a verbatim body may exceed one SMS segment; lets the channel layer split it. */
  allowSplit?: boolean;
  /** Voice only. */
  voicemail?: BotSequenceConfigStepVoicemail;
}

/**
 * A per-day one-way email (ENG-3216 decision 12). NOT a channel and NOT a delivery: it
 * opens no conversation. Content and sending account are pending decision 41; the shape
 * stores an authored subject/body so the editor can round-trip either outcome.
 */
export interface BotSequenceConfigStepEmail {
  enabled: boolean;
  /** `{{ }}` variables allowed. */
  subject: string;
  /** Plain text with line breaks; `{{ }}` variables allowed. */
  body: string;
}

/**
 * One FMS tenant note per step fire (ENG-3216 decisions 17/18), written after the day's
 * deliveries resolve. `text` is a `{{ }}` template; blank = `BOT_SEQUENCE_FMS_NOTE_DEFAULT_TEXT`.
 */
export interface BotSequenceConfigStepFmsNote {
  enabled: boolean;
  text: string;
}

/**
 * One step of the ladder — one DAY of the sequence as the operator sees it. Everything on
 * it is a setting the engine acts on (ENG-3216).
 */
export interface BotSequenceConfigStep {
  /** UI identity only (React keys, drafts). The cursor is offset-based and untouched (decision 37). */
  key: string;
  /**
   * Days from the enrollment anchor (Day 0). Fixed once saved (decision 37): the editor
   * never renumbers a day — remove it and add another instead. Unique within a ladder.
   */
  offsetDays: number;
  /** A disabled step is not due and never fires while disabled. */
  enabled: boolean;
  /**
   * Omitted = one generated SMS delivery. `[]` is legal ONLY when `email.enabled` (an
   * email-only day); an enabled day must send something.
   */
  deliveries?: BotSequenceConfigStepDelivery[];
  email?: BotSequenceConfigStepEmail;
  fmsNote?: BotSequenceConfigStepFmsNote;
  /**
   * Default true. `false` = "Always send on this day": ignores schedule closures, min gap
   * and agent suppression; never the quiet-hours floor (decisions 9/14/16).
   */
  deferrable?: boolean;
  /** "Send immediately": fire at the next legal moment instead of a spread position. Legal ONLY on MOVE_IN / MOVE_OUT (decision 8). */
  sendASAP?: boolean;
  /**
   * The call script a reply to THIS day's message starts with (ENG-3403). A slug from the org's
   * scripts (`call_script.slug` or the platform Sales slug); omitted = no starting script, the bot
   * picks from every script in force as usual. The conversation the fire opens is stamped
   * `originatingSequenceStepId = key`, and the prompt renders that script first.
   */
  callScriptSlug?: string;
}

/**
 * One SequenceConfig row. Every settings field is nullable: null = "no preference at
 * this scope", i.e. inherit from the next scope up. Exactly one of groupId/locationId
 * is set, or neither for the org-level default.
 */
export interface BotSequenceConfigInterface extends OrgBaseInterface {
  groupId: number | null;
  locationId: number | null;
  sequenceType: BotSequenceType;
  /** ENABLED / SIMULATED / DISABLED. Unresolved at every scope = the type is not configured (books nothing). */
  mode: BotSequenceMode | null;
  /** The whole ladder or null to inherit — never merged across scopes. Empty arrays are rejected at write time. */
  ladder: BotSequenceConfigStep[] | null;
  /**
   * Which schedule supplies the send hours (and closures) for this type. Unresolved at
   * every scope = `BOT_SEQUENCE_DEFAULT_SCHEDULE_SLUG` ("sequence") — one shared form
   * per location; per-type slugs exist for future-proofing.
   */
  scheduleSlug: string | null;
  /** Minimum days between fires for ONE enrollment (never across enrollments); unresolved = 0 (off). */
  minGapDays: number | null;
  /**
   * PROD-559: raw FMS lease statuses opted back into past-due outreach (PAST_DUE rows
   * only). Unresolved = the legacy-free `SequencePastDueProcessInactiveStatuses`
   * location variable keeps supplying the value.
   */
  pastDueInactiveOptInStatuses: string[] | null;
}

// ── API shapes ─────────────────────────────────────────────────────────────────────

/** Where a resolved field's value came from. `default` = no scope expressed a preference. */
export type BotSequenceConfigFieldSource = "location" | "group" | "org" | "default";

/**
 * The write shape: everything an operator may set on one (scope, type) row — and the
 * body of `PUT /sequence-config/org/{orgId}/{sequenceType}?groupId=&locationId=`, with
 * PARTIAL-update semantics: an omitted field is untouched, an explicit `null` clears the
 * field at the scope (back to inheriting), a value sets it. A field editor therefore
 * sends only its own field and never has to think about the others. Scope is addressed
 * by the query params: at most one of groupId/locationId; neither = the org default row.
 */
export interface BotSequenceConfigValues {
  mode?: BotSequenceMode | null;
  ladder?: BotSequenceConfigStep[] | null;
  scheduleSlug?: string | null;
  minGapDays?: number | null;
  pastDueInactiveOptInStatuses?: string[] | null;
}

/** One resolved field with its provenance — what the override UI renders per field. */
export interface BotSequenceConfigEffectiveField<T> {
  value: T;
  source: BotSequenceConfigFieldSource;
}

/**
 * ENG-3512 (legacy-send-cutover.md §6): a location under the read scope where the LEGACY sequence
 * engine is still enabled for the type — the org has a legacy `sequence` row of that type whose
 * step and the location's effective enabled variable for that step reads ENABLED. Temporary —
 * deleted with the legacy engine.
 */
export interface BotSequenceLegacyEnabledLocation {
  locationId: number;
  locationName: string;
}

/** The effective (fully cascaded) config of one SCOPE (org/group/location) for one sequence type. */
export interface BotSequenceConfigEffective {
  /** Set when the requested scope is a location; null for group/org-scope reads. */
  locationId: number | null;
  sequenceType: BotSequenceType;
  /** `value: null` = the type is not configured at any scope — the scheduler books nothing. */
  mode: BotSequenceConfigEffectiveField<BotSequenceMode | null>;
  ladder: BotSequenceConfigEffectiveField<BotSequenceConfigStep[] | null>;
  /** `source: "default"` = no scope expressed a preference; the shared "sequence" schedule applies. */
  scheduleSlug: BotSequenceConfigEffectiveField<string>;
  minGapDays: BotSequenceConfigEffectiveField<number>;
  /** `source: "default"` = the location-variable fallback supplied the value (or nothing did). */
  pastDueInactiveOptInStatuses: BotSequenceConfigEffectiveField<string[] | null>;
  /**
   * ENG-3512: the locations that would become ENABLED for this type if this scope were saved
   * ENABLED and where the legacy engine is still enabled. Non-empty = the API refuses
   * `mode: ENABLED` here and Studio disables that option with a hint. Always present.
   */
  legacyEnabledLocations: BotSequenceLegacyEnabledLocation[];
}

/**
 * The FMS type behind every location in a scope (ENG-3216 decision 25). The page reads
 * `FMS_CAPABILITY_MATRIX` itself via `hasCapability`; this only tells it WHICH row(s) to
 * read, resolved the way the engine resolves it (the inherited `FMSIntegrationId` variable).
 */
export interface BotSequenceScopeFmsTypes {
  groupId: number | null;
  locationId: number | null;
  /** Every location in the addressed scope (one entry at location scope). */
  locationIds: number[];
  /** locationId → FMS type, for the locations that resolve to an integration; absent = no FMS. */
  fmsTypeByLocationId: Record<number, FMSType>;
  /** True when the org has an active Mandrill App — the account sequence emails go out from (decision 41). */
  emailSenderConfigured: boolean;
}
